import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch, Redirect, useLocation } from 'react-router-dom';
import TV_product from './product';
import {
	DashboardOutlined,
	UsergroupAddOutlined,
	ProductOutlined,
	UnorderedListOutlined,
	FileTextOutlined,
	MenuUnfoldOutlined,
	MenuFoldOutlined,
	FolderOpenOutlined,
	FilePptOutlined,
	ProjectOutlined,
	UsergroupDeleteOutlined,
	DatabaseOutlined,
	SettingOutlined,
	AppstoreOutlined,
	ReadOutlined,
	ArrowUpOutlined,
	ArrowDownOutlined,
} from '@ant-design/icons';
import { Layout, Menu, Typography, Button, Grid, Tooltip } from 'antd';
import SH_header_admin from './Header';
import SH_news from './News';
import Dashboard from './Dashboard';
import TV_user from './user/index';
import TV_category from './category';
import TV_new_category from './new-category';
import TV_menu from './menu';
import TV_page from './page';
import TV_project from './project';
import TV_partner from './partner';
import SectionPremium from './section';
import TV_backup from './backup';

const { Title } = Typography;
const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const SIDEBAR_EXPANDED = 220;
const SIDEBAR_COLLAPSED = 72;

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
const scrollToBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });

function Admin() {
	const isAdmin = Boolean(JSON.parse(localStorage.getItem('isAdmin')));
	const { url } = useRouteMatch();
	const history = useHistory();
	const location = useLocation();
	const screens = useBreakpoint();

	const isMobile = !screens.lg;

	const [collapsed, setCollapsed] = useState(() => (isMobile ? true : false));

	useEffect(() => {
		if (isMobile) setCollapsed(true);
	}, [isMobile]);

	const animLockRef = useRef(false);
	const toggleCollapsed = () => {
		if (animLockRef.current) return;
		animLockRef.current = true;
		setCollapsed((prev) => !prev);
		window.setTimeout(() => {
			animLockRef.current = false;
		}, 220);
	};

	const selectedKey = useMemo(() => {
		const p = location.pathname || '';
		const match = p.match(/\/admin\/([^/]+)/);
		return match?.[1] || 'dashboard';
	}, [location.pathname]);

	const openKeyByItem = useMemo(
		() => ({
			dashboard: 'group_overview',
			products: 'group_product',
			'product-category': 'group_product',
			news: 'group_content',
			'news-category': 'group_content',
			projects: 'group_project',
			partner: 'group_project',
			menu: 'group_site',
			page: 'group_site',
			sections: 'group_site',
			user: 'group_system',
			backup: 'group_system',
		}),
		[]
	);

	const [openKeys, setOpenKeys] = useState(() => {
		const ok = openKeyByItem[selectedKey];
		return ok ? [ok] : [];
	});

	useEffect(() => {
		if (!collapsed) {
			const ok = openKeyByItem[selectedKey];
			setOpenKeys(ok ? [ok] : []);
		}
	}, [selectedKey, collapsed, openKeyByItem]);

	const menuItems = useMemo(() => {
		const systemChildren = [
			{
				key: 'user',
				label: 'Quản lý tài khoản',
				icon: <UsergroupAddOutlined />,
				style: isAdmin ? {} : { display: 'none' },
			},
			{
				key: 'backup',
				label: 'Backup DB',
				icon: <DatabaseOutlined />,
				style: isAdmin ? {} : { display: 'none' },
			},
		].filter((x) => x.style?.display !== 'none');

		return [
			{
				key: 'group_overview',
				label: 'Tổng quan',
				icon: <AppstoreOutlined />,
				children: [{ key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> }],
			},
			{
				key: 'group_product',
				label: 'Sản phẩm',
				icon: <ProductOutlined />,
				children: [
					{ key: 'products', label: 'Quản lý sản phẩm', icon: <ProductOutlined /> },
					{ key: 'product-category', label: 'Loại sản phẩm', icon: <UnorderedListOutlined /> },
				],
			},
			{
				key: 'group_content',
				label: 'Nội dung',
				icon: <ReadOutlined />,
				children: [
					{ key: 'news', label: 'Tin tức', icon: <FileTextOutlined /> },
					{ key: 'news-category', label: 'Loại tin tức', icon: <UnorderedListOutlined /> },
				],
			},
			{
				key: 'group_project',
				label: 'Dự án & Đối tác',
				icon: <ProjectOutlined />,
				children: [
					{ key: 'projects', label: 'Quản lý dự án', icon: <ProjectOutlined /> },
					{ key: 'partner', label: 'Quản lý đối tác', icon: <UsergroupDeleteOutlined /> },
				],
			},
			{
				key: 'group_site',
				label: 'Website',
				icon: <MenuUnfoldOutlined />,
				children: [
					{ key: 'menu', label: 'Quản lý menu', icon: <MenuUnfoldOutlined /> },
					{ key: 'page', label: 'Quản lý page', icon: <FolderOpenOutlined /> },
					{ key: 'sections', label: 'Quản lý khối nội dung', icon: <FilePptOutlined /> },
				],
			},
			...(systemChildren.length
				? [
						{
							key: 'group_system',
							label: 'Hệ thống',
							icon: <SettingOutlined />,
							children: systemChildren,
						},
					]
				: []),
		];
	}, [isAdmin]);

	// fixed toggle position depends on siderWidth
	const siderWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

	const [showScrollBtns, setShowScrollBtns] = useState(false);
	const [atBottom, setAtBottom] = useState(false);

	useEffect(() => {
		let ticking = false;

		const onScroll = () => {
			if (ticking) return;
			ticking = true;

			requestAnimationFrame(() => {
				const doc = document.documentElement;
				const scrollTop = doc.scrollTop || document.body.scrollTop || 0;
				const maxScroll = doc.scrollHeight - doc.clientHeight;

				setShowScrollBtns(scrollTop > 220);
				setAtBottom(scrollTop >= maxScroll - 4);

				ticking = false;
			});
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	return (
		<Layout style={{ minHeight: '100vh' }}>
			{/* Header */}
			<Header
				style={{
					position: 'sticky',
					top: 0,
					zIndex: 10,
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					gap: 12,
					paddingInline: 16,
				}}
			>
				<Button
					type="text"
					onClick={toggleCollapsed}
					icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
					style={{
						color: '#fff',
						width: 44,
						height: 44,
						borderRadius: 12,
					}}
				/>
				<div style={{ flex: 1 }}>
					<SH_header_admin />
				</div>
			</Header>

			<Layout>
				<div style={{ position: 'relative' }}>
					<Sider
						collapsible
						collapsed={collapsed}
						collapsedWidth={SIDEBAR_COLLAPSED}
						width={SIDEBAR_EXPANDED}
						trigger={null}
						style={{
							overflow: 'auto',
							height: 'calc(100vh - 64px)',
							position: 'sticky',
							top: 64,
							left: 0,
							boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
							transition: 'all .2s ease',
							willChange: 'width',
						}}
					>
						{/* Brand aligned with menu icon column */}
						<div
							style={{
								height: 56,
								display: 'flex',
								alignItems: 'center',
								gap: 10,
								paddingLeft: 24,
								paddingRight: 16,
								color: '#fff',
								fontWeight: 700,
								letterSpacing: 0.2,
								borderBottom: '1px solid rgba(255,255,255,0.08)',
							}}
						>
							<div
								style={{
									width: 24,
									height: 24,
									borderRadius: 8,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									background: 'rgba(255,255,255,0.12)',
									fontSize: 13,
									lineHeight: '24px',
									flex: '0 0 24px',
								}}
							>
								SH
							</div>

							{!collapsed && <span style={{ fontSize: 15, lineHeight: '24px' }}>SunHairCompany Admin</span>}
						</div>

						<Menu
							theme="dark"
							mode="inline"
							items={menuItems}
							selectedKeys={[selectedKey]}
							inlineCollapsed={collapsed}
							openKeys={collapsed ? [] : openKeys}
							onOpenChange={(keys) => setOpenKeys(keys)}
							onClick={(e) => history.push(`/admin/${e.key}`)}
							style={{ paddingBottom: 16 }}
						/>
					</Sider>

					{/* Side-edge toggle button (fixed) */}
					{!isMobile && (
						<Tooltip title={collapsed ? 'Mở menu' : 'Thu menu'} placement="right">
							<Button
								onClick={toggleCollapsed}
								icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
								shape="circle"
								style={{
									position: 'fixed',
									top: 'calc(64px + 30vh)',
									left: siderWidth - 16,
									transform: 'translateY(-50%)',
									zIndex: 999,

									width: 34,
									height: 34,
									borderRadius: 999,
									boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
									background: '#fff',

									transition: 'left .2s ease, top .2s ease',
								}}
							/>
						</Tooltip>
					)}
				</div>

				{/* Content */}
				<Layout
					style={{
						transition: 'all .2s ease',
						padding: 16,
						minHeight: 'calc(100vh - 64px)',
						background: '#f5f7fb',
					}}
				>
					<Content
						style={{
							borderRadius: 16,
							background: '#fff',
							padding: 16,
							boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
						}}
					>
						<Switch>
							<Route exact path={`${url}/dashboard`} component={Dashboard} />
							<Route path={`${url}/products`} component={TV_product} />
							<Route path={`${url}/projects`} component={TV_project} />
							<Route path={`${url}/news`} component={SH_news} />
							<Route path={`${url}/user`} component={TV_user} />
							<Route path={`${url}/product-category`} component={TV_category} />
							<Route path={`${url}/news-category`} component={TV_new_category} />
							<Route path={`${url}/menu`} component={TV_menu} />
							<Route path={`${url}/sections`} component={SectionPremium} />
							<Route path={`${url}/page`} component={TV_page} />
							<Route path={`${url}/partner`} component={TV_partner} />
							<Route path={`${url}/page-sections-link`} component={TV_new_category} />
							<Route path={`${url}/backup`} component={TV_backup} />
							<Redirect exact from={`${url}`} to={`${url}/dashboard`} />
							<Route render={() => <Title>404 - Không tìm thấy trang</Title>} />
						</Switch>
					</Content>
				</Layout>
			</Layout>

			{/* Scroll buttons (fixed - right) */}
			{showScrollBtns && (
				<div
					style={{
						position: 'fixed',
						right: 18,
						bottom: 18,
						zIndex: 999,
						display: 'flex',
						flexDirection: 'column',
						gap: 10,
					}}
				>
					{/* Scroll Down */}
					{!atBottom && (
						<Tooltip title="Xuống cuối" placement="left">
							<Button
								shape="circle"
								icon={<ArrowDownOutlined />}
								onClick={scrollToBottom}
								style={{
									width: 40,
									height: 40,
									borderRadius: 999,
									boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
								}}
							/>
						</Tooltip>
					)}

					{/* Scroll Up */}
					<Tooltip title="Lên đầu" placement="left">
						<Button
							shape="circle"
							type="primary"
							icon={<ArrowUpOutlined />}
							onClick={scrollToTop}
							style={{
								width: 40,
								height: 40,
								borderRadius: 999,
								boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
							}}
						/>
					</Tooltip>
				</div>
			)}
		</Layout>
	);
}

export default Admin;
